import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import Layout from "@/components/Layout";
import { manualApi, ManualInfo } from "@/api/modules/manual";
import { 
  Search, 
  Download, 
  FileText, 
  ChevronRight,
  Loader2
} from "lucide-react";

const Manual = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("-1");
  const [manuals, setManuals] = useState<ManualInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const categories = [
    { id: "-1", name: "全部" },
    { id: "1", name: "SeeOD" },
    { id: "3", name: "SeeTF" },
    { id: "4", name: "教程" }
  ];

  const fetchManuals = async () => {
    setLoading(true);
    try {
      const response = await manualApi.getManualList({
        pageNum: 1,
        pageSize: 10,
        category: selectedCategory === "-1" ? undefined : parseInt(selectedCategory),
        manualName: searchTerm || undefined
      });
      setManuals(response.rows || []);
      setTotal(response.total || 0);
    } catch (error) {
      console.error("获取手册列表失败:", error);
      setManuals([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchManuals();
  }, [selectedCategory, searchTerm]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const getCategoryName = (categoryId: number) => {
    const category = categories.find(c => c.id === categoryId.toString());
    return category?.name || "未知";
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const getFileExtension = (path: string): string => {
    const lastDotIndex = path.lastIndexOf('.');
    if (lastDotIndex > 0) {
      return path.substring(lastDotIndex);
    }
    return '';
  };

  const handleDownload = async (manual: ManualInfo) => {
    try {
      const blob = await manualApi.downloadManual(manual.id);
      
      const extension = getFileExtension(manual.manualPath);
      const fileName = extension ? 
        (manual.manualName.endsWith(extension) ? manual.manualName : manual.manualName + extension) :
        manual.manualName;
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
    } catch (error) {
      console.error("下载失败:", error);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Badge variant="outline" className="mb-3 text-sm border-blue-300 text-blue-700 bg-blue-50">
              文档中心
            </Badge>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              参考手册
            </h1>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              提供完整的产品文档、教程和技术资料，助您快速掌握软件使用方法
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="搜索文档..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-12 pr-4 py-2.5"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Categories */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">文档分类</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="space-y-1">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${
                        selectedCategory === category.id
                          ? "bg-blue-50 text-blue-600 border-r-2 border-blue-600 font-medium"
                          : "text-gray-600"
                      }`}
                    >
                      <span>{category.name}</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Quick Links */}
            <Card className="mt-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">快速链接</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <Link 
                  to="/trial" 
                  className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                  <span>产品下载</span>
                </Link>
                {/* <Link 
                  to="/community" 
                  className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                  <span>技术社区</span>
                </Link> */}
                <Link 
                  to="/contact" 
                  className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                  <span>技术支持</span>
                </Link>
              </CardContent>
            </Card>
          </div>

          {/* Main Content - Manuals List */}
          <div className="lg:col-span-3">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                {selectedCategory === "-1" ? "全部文档" : categories.find(c => c.id === selectedCategory)?.name}
                <span className="text-gray-500 text-sm ml-2">({total})</span>
              </h2>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : (
              <div className="space-y-4">
                {manuals.map((manual) => (
                  <Card key={manual.id} className="hover:shadow-md transition-shadow duration-300">
                    <CardContent className="p-5">
                      <div className="flex items-start space-x-4">
                        {/* Icon */}
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600">
                            <FileText className="w-5 h-5" />
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="text-base font-semibold text-gray-900 mb-1.5">
                                {manual.manualName}
                              </h3>
                              <p className="text-gray-600 mb-2.5 leading-relaxed text-sm line-clamp-2">
                                {manual.manualDesc}
                              </p>
                              
                              {/* Tags */}
                              <div className="flex flex-wrap gap-2 mb-3">
                                <Badge variant="outline" className="text-xs">
                                  <FileText className="w-3.5 h-3.5 mr-1" />
                                  <span>{getCategoryName(manual.category)}</span>
                                </Badge>
                                {manual?.tags?.split(',').map((tag, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-xs">
                                    {tag.trim()}
                                  </Badge>
                                ))}
                              </div>

                              {/* Meta Info */}
                              <div className="flex items-center space-x-4 text-xs text-gray-500">
                                <div className="flex items-center space-x-1">
                                  <Download className="w-3.5 h-3.5" />
                                  <span>{manual.downloadCount} 次下载</span>
                                </div>
                                <div>
                                  <span>{formatFileSize(manual.manualSize)}</span>
                                </div>
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex-shrink-0 ml-4">
                              <Button 
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700 text-sm px-4"
                                onClick={() => handleDownload(manual)}
                              >
                                <Download className="w-3.5 h-3.5 mr-1.5" />
                                下载
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {!loading && manuals.length === 0 && (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h3 className="text-base font-medium text-gray-900 mb-1.5">未找到相关文档</h3>
                <p className="text-gray-500 text-sm">
                  请尝试调整搜索关键词或选择其他分类
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            需要更多帮助？
          </h2>
          <p className="text-base text-gray-600 mb-6">
            我们的技术团队随时为您提供专业的技术支持
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="default" asChild>
              <Link to="/contact">
                联系技术支持
              </Link>
            </Button>
            {/* <Button size="default" variant="outline" asChild>
              <Link to="/community">
                加入技术社区
              </Link>
            </Button> */}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Manual;